import React, { useState, useEffect, useRef } from 'react';
import { HiPencil, HiEye, HiCheck, HiX, HiPlus, HiSearch, HiDocumentText, HiUserAdd } from 'react-icons/hi';
import { QRCodeSVG } from 'qrcode.react';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import ConfirmationModal from '../components/ConfirmationModal';
import ReceiveWorkOrderModal from '../components/ReceiveWorkOrderModal';
import Tooltip from '../components/Tooltip';
import VariantSelect from '../components/VariantSelect';
import CitySelect from '../components/CitySelect';
import { getErrorMessage } from '../utils/errorHandler';
import { formatDate } from '../utils/dateFormatter';
import { formatPKR } from '../utils/currencyFormatter';
import { formatPartySearchBalanceLabel, partySearchBalanceClassName } from '../utils/partySearchBalance';
import { API } from '../config';
import axios from 'axios';
import './WorkOrders.css';

/**
 * Work Orders Component
 * 
 * Workflow:
 * 1. Purchase unstitched products → Creates vendor ledger entry (payable)
 * 2. Create Work Order → Links to purchase item (vendor batch) and assigns tailor
 * 3. Issue Work Order → Reduces unstitched inventory, creates vendor payable ledger entry
 * 4. Receive Work Order → Tailor sends bill and stitched units, creates tailor payable ledger entry, adds stitched units to inventory
 * 5. Close Work Order → Finalizes costs and updates inventory cost prices
 * 
 * Traceability Chain:
 * Vendor → Purchase → Work Order → Tailor → Stitched Units (inventory)
 * 
 * Each stitched unit can be traced back to:
 * - Who supplied it (vendor)
 * - Which tailor stitched it
 * - Which purchase batch it came from
 */
const WorkOrders = () => {
  const { user } = useAuth();
  const { hasBranches, effectiveBranchId, isAdmin, currentBranch } = useBranch();
  const isMainBranch = currentBranch && (currentBranch.is_main === true || currentBranch.isMain === true);
  const { success, error } = useNotification();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [workOrderForReceiveModal, setWorkOrderForReceiveModal] = useState(null); // Full work order with output_product + meta for receive
  const [actionType, setActionType] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(user.business_id || user.businessId || '');
  const [branches, setBranches] = useState([]);
  const [tailors, setTailors] = useState([]);
  const [inputProducts, setInputProducts] = useState([]);
  const [inputVariants, setInputVariants] = useState([]);
  const [productInventory, setProductInventory] = useState({}); // Store inventory by productId
  const [variantInventory, setVariantInventory] = useState({}); // Store inventory by variantId
  const [showAddTailorModal, setShowAddTailorModal] = useState(false);
  const [tailorFormData, setTailorFormData] = useState({
    name: '',
    phone: '',
    city: '',
    address: '',
    openingBalanceDirection: 'you_owe_them',
    openingBalanceAmount: ''
  });
  const [formData, setFormData] = useState({
    tailorId: '',
    inputProductId: '',
    inputItems: [{ variantId: '', quantity: '' }], // Multiple variant + quantity rows
    outputProductId: '', // Our Creation – existing product
    outputProductName: '', // Our Creation – new product name
    outputProductCode: '', // Our Creation – new product code
    tailoringCost: '',
    expectedOutputQuantity: '', // Optional: how many items expected as return
    notes: ''
  });
  const [outputProductMode, setOutputProductMode] = useState('existing'); // 'existing' | 'new'
  const [submitting, setSubmitting] = useState(false);
  const [tailorSearchQuery, setTailorSearchQuery] = useState('');
  const [showTailorDropdown, setShowTailorDropdown] = useState(false);
  const tailorDropdownRef = useRef(null);

  const [inputProductSearchQuery, setInputProductSearchQuery] = useState('');
  const [showInputProductDropdown, setShowInputProductDropdown] = useState(false);
  const inputProductDropdownRef = useRef(null);

  const [outputProductSearchQuery, setOutputProductSearchQuery] = useState('');
  const [showOutputProductDropdown, setShowOutputProductDropdown] = useState(false);
  const [outputProductSearchResults, setOutputProductSearchResults] = useState([]);
  const [outputProductSearchLoading, setOutputProductSearchLoading] = useState(false);
  const outputProductDropdownRef = useRef(null);

  // Close tailor dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tailorDropdownRef.current && !tailorDropdownRef.current.contains(e.target)) {
        setShowTailorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close input product dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputProductDropdownRef.current && !inputProductDropdownRef.current.contains(e.target)) {
        setShowInputProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close output product dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (outputProductDropdownRef.current && !outputProductDropdownRef.current.contains(e.target)) {
        setShowOutputProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredTailors = tailors.filter(t => {
    const q = (tailorSearchQuery || '').toLowerCase().trim();
    if (!q) return true;
    const name = (t.name || '').toLowerCase();
    const city = (t.city || '').toLowerCase();
    const phone = (t.phone || '').replace(/\D/g, '');
    const phoneQuery = q.replace(/\D/g, '');
    return name.includes(q) || city.includes(q) || (phoneQuery && phone.includes(phoneQuery));
  });

  const selectedTailor = tailors.find(t => t.id.toString() === formData.tailorId);
  const tailorDisplayText = selectedTailor
    ? `${selectedTailor.name}${selectedTailor.city ? ` (${selectedTailor.city})` : ''}`
    : '';

  // All unstitched products, filtered by search and by non-zero inventory (branch selected)
  const filteredInputProducts = inputProducts.filter(p => {
    const q = (inputProductSearchQuery || '').toLowerCase().trim();
    const hasStock = (productInventory[p.id] ?? 0) > 0;
    if (effectiveBranchId && productInventory[p.id] !== undefined && !hasStock) return false;
    if (!q) return true;
    const name = (p.name || '').toLowerCase();
    const code = (p.product_code || '').toLowerCase();
    return name.includes(q) || code.includes(q);
  });
  const selectedInputProduct = inputProducts.find(p => p.id.toString() === formData.inputProductId);
  const inputProductDisplayText = selectedInputProduct
    ? `${selectedInputProduct.name} (${selectedInputProduct.product_code})`
    : '';

  const [selectedOutputProduct, setSelectedOutputProduct] = useState(null);
  const outputProductDisplayText = selectedOutputProduct
    ? `${selectedOutputProduct.name} (${selectedOutputProduct.product_code || selectedOutputProduct.productCode || ''})`
    : '';

  useEffect(() => {
    if (user.role === 'super_admin') {
      fetchBusinesses();
    } else {
      fetchBranches();
      fetchTailors();
      fetchProducts();
    }
  }, [user.role]);

  useEffect(() => {
    if (selectedBusinessId) {
      fetchWorkOrders();
      if (user.role === 'super_admin') {
        fetchBranches();
        fetchTailors();
        fetchProducts();
      }
    }
  }, [selectedBusinessId, statusFilter, effectiveBranchId]);

  // Fetch variants when product selected; use header-selected branch for stock
  useEffect(() => {
    if (formData.inputProductId) {
      fetchProductVariants(formData.inputProductId, effectiveBranchId || null);
      if (effectiveBranchId) {
        fetchInventoryForProduct(formData.inputProductId, effectiveBranchId);
      } else {
        setVariantInventory({});
      }
    } else {
      setInputVariants([]);
      setVariantInventory({});
    }
  }, [formData.inputProductId, effectiveBranchId]);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/businesses/all`);
      setBusinesses(response.data.businesses || []);
      if (response.data.businesses && response.data.businesses.length > 0 && !selectedBusinessId) {
        setSelectedBusinessId(response.data.businesses[0].id);
      }
    } catch (err) {
      console.error('Error fetching businesses:', err);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      const businessId = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
      if (!businessId) {
        setLoading(false);
        return;
      }
      const params = { ...(statusFilter && { status: statusFilter }), ...(effectiveBranchId && { branchId: effectiveBranchId }) };
      const response = await axios.get(
        `${API}/work-orders/business/${businessId}`,
        { params }
      );
      setWorkOrders(response.data.work_orders || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching work orders:', err);
      error(getErrorMessage(err));
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
      if (!businessId) return;
      let branchesData = [];
      if (user.role === 'super_admin') {
        const response = await axios.get(`${API}/businesses/${businessId}/branches`);
        branchesData = response.data.branches || [];
      } else if (user.role === 'admin') {
        const response = await axios.get(`${API}/branches`);
        branchesData = response.data.branches || [];
      } else {
        // Manager - use their branch
        const branchId = user.branch_id || user.branchId;
        branchesData = [{ id: branchId, name: user.branch?.name || 'My Branch' }];
      }
      setBranches(branchesData);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const fetchTailors = async () => {
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
      if (!businessId) return;
      const params = new URLSearchParams({ role: 'tailor' });
      if (effectiveBranchId) params.set('branchId', effectiveBranchId);
      const response = await axios.get(`${API}/parties/business/${businessId}?${params.toString()}`);
      setTailors(response.data.parties || []);
    } catch (err) {
      console.error('Error fetching tailors:', err);
    }
  };

  const fetchOutputProductSearch = async (query) => {
    const q = (query || '').trim();
    if (q.length < 2) {
      setOutputProductSearchResults([]);
      return;
    }
    try {
      setOutputProductSearchLoading(true);
      const businessId = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
      if (!businessId) return;
      const params = new URLSearchParams({ q, productType: 'stitched', limit: 15 });
      if (effectiveBranchId) params.set('branchId', effectiveBranchId);
      const res = await axios.get(`${API}/products/business/${businessId}/search?${params.toString()}`);
      const list = res.data?.products || [];
      setOutputProductSearchResults(list.filter((p) => (p.product_type || p.productType) === 'stitched'));
    } catch (err) {
      setOutputProductSearchResults([]);
    } finally {
      setOutputProductSearchLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
      if (!businessId) return;
      
      // Fetch unstitched products (input)
      const productParams = new URLSearchParams({ productType: 'unstitched' });
      if (effectiveBranchId) productParams.set('branchId', effectiveBranchId);
      const inputResponse = await axios.get(`${API}/products/business/${businessId}?${productParams.toString()}`);
      const products = inputResponse.data.products || [];
      setInputProducts(products);
      
      // Initialize inventory map: use display_quantity (variant stock sum) for unstitched with variants, else product.quantity
      const initialInventoryMap = {};
      products.forEach(product => {
        const displayQty = product.display_quantity ?? product.displayQuantity;
        const qty = product.quantity;
        const value = displayQty != null ? parseFloat(displayQty) : parseFloat(qty);
        initialInventoryMap[product.id] = (typeof value === 'number' && !isNaN(value)) ? value : 0;
      });
      setProductInventory(initialInventoryMap);
      
      // Fetch branch-specific inventory when header branch is selected
      if (effectiveBranchId) {
        await fetchInventoryForProducts(products, effectiveBranchId);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchInventoryForProducts = async (products, branchId) => {
    try {
      const inventoryMap = {};
      const variantMap = {};
      
      // Fetch branch inventory
      const inventoryResponse = await axios.get(`${API}/inventory/branch/${branchId}`);
      const inventoryItems = inventoryResponse.data.inventory || [];
      
      // Build variant-level inventory
      inventoryItems.forEach(item => {
        const vid = item.variant_id ?? item.variantId;
        if (vid) {
          variantMap[vid] = (variantMap[vid] || 0) + parseFloat(item.quantity || 0);
        }
      });
      
      // Group inventory by productId (sum all variants, or use product.quantity for products without variants)
      products.forEach(product => {
        const variants = product.variants || [];
        let inventoryFromTable = 0;
        if (variants.length > 0) {
          variants.forEach(v => {
            const qty = variantMap[v.id] || 0;
            inventoryFromTable += qty;
          });
        } else {
          inventoryFromTable = inventoryItems
            .filter(item => (item.product_id || item.productId) === product.id)
            .reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
        }
        
        const productQty = inventoryFromTable > 0 ? inventoryFromTable : (parseFloat(product.quantity) || 0);
        inventoryMap[product.id] = productQty;
      });
      
      setProductInventory(inventoryMap);
      setVariantInventory(variantMap);
    } catch (err) {
      console.error('Error fetching inventory:', err);
      // On error, fall back to display_quantity (or product.quantity for products without variants)
      const fallbackMap = {};
      products.forEach(product => {
        const displayQty = product.display_quantity ?? product.displayQuantity;
        const qty = displayQty != null ? parseFloat(displayQty) : parseFloat(product.quantity);
        fallbackMap[product.id] = (typeof qty === 'number' && !isNaN(qty)) ? qty : 0;
      });
      setProductInventory(fallbackMap);
    }
  };

  const fetchInventoryForProduct = async (productId, branchId) => {
    if (!productId || !branchId) return;
    
    try {
      // Fetch branch inventory
      const inventoryResponse = await axios.get(`${API}/inventory/branch/${branchId}`);
      const inventoryItems = inventoryResponse.data.inventory || [];
      
      const productIdNum = parseInt(productId);
      const productItems = inventoryItems.filter(item => {
        const itemProductId = item.product_id ?? item.productId;
        return parseInt(itemProductId) === productIdNum;
      });
      
      // Sum inventory for all variants of this product
      const inventoryFromTable = productItems.reduce(
        (sum, item) => sum + parseFloat(item.quantity || 0),
        0
      );
      
      // Build variant-level inventory for this product
      const variantMap = {};
      productItems.forEach(item => {
        const vid = item.variant_id ?? item.variantId;
        if (vid) {
          variantMap[vid] = (variantMap[vid] || 0) + parseFloat(item.quantity || 0);
        }
      });
      
      const product = inputProducts.find(p => p.id === productId);
      const productInventory = inventoryFromTable > 0 
        ? inventoryFromTable 
        : (product ? parseFloat(product.quantity) || 0 : 0);
      
      setProductInventory(prev => ({ ...prev, [productId]: productInventory }));
      setVariantInventory(prev => ({ ...prev, ...variantMap }));
    } catch (err) {
      console.error('Error fetching inventory for product:', err);
      const product = inputProducts.find(p => p.id === productId);
      if (product) {
        setProductInventory(prev => ({
          ...prev,
          [productId]: parseFloat(product.quantity) || 0
        }));
      }
    }
  };

  const fetchProductVariants = async (productId, branchId) => {
    try {
      const url = branchId
        ? `${API}/products/${productId}?branchId=${branchId}`
        : `${API}/products/${productId}`;
      const response = await axios.get(url);
      const product = response.data.product || {};
      const variants = product.variants || [];
      setInputVariants(variants);
      // Populate variant inventory from API when branch selected (variants include available_quantity)
      if (branchId && variants.length > 0) {
        const map = {};
        variants.forEach(v => {
          const qty = v.available_quantity ?? v.availableQuantity;
          if (v.id != null && (qty != null || qty === 0)) map[v.id] = parseFloat(qty) || 0;
        });
        if (Object.keys(map).length > 0) {
          setVariantInventory(prev => ({ ...prev, ...map }));
        }
      }
      if (variants.length === 1) {
        setFormData(prev => ({ ...prev, inputVariantId: variants[0].id }));
      }
    } catch (err) {
      console.error('Error fetching product variants:', err);
    }
  };

  const handleCreateTailor = async (e) => {
    e.preventDefault();
    if (isAdmin && !effectiveBranchId) {
      error('Select a branch from the header dropdown before adding a tailor.');
      return;
    }
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
      const branchId = user.role === 'manager' ? (user.branch_id || user.branchId) : effectiveBranchId;
      const amount = parseFloat(tailorFormData.openingBalanceAmount) || 0;
      const dir = tailorFormData.openingBalanceDirection;
      const openingBalance = (dir === 'they_owe_you' ? amount : dir === 'you_owe_them' ? -amount : 0);
      const payload = {
        name: tailorFormData.name,
        phone: tailorFormData.phone,
        city: tailorFormData.city,
        address: tailorFormData.address,
        openingBalance,
        isVendor: false,
        isCustomer: false,
        isTailor: true,
        isAdditional: false,
        businessId: parseInt(businessId)
      };
      if (branchId != null) {
        payload.branchId = parseInt(branchId, 10);
      }

      const response = await axios.post(`${API}/parties`, payload);
      success('Tailor created successfully!');
      setShowAddTailorModal(false);
      setTailorFormData({ name: '', phone: '', city: '', address: '', openingBalanceDirection: 'you_owe_them', openingBalanceAmount: '' });
      await fetchTailors();
      // Auto-select the newly created tailor
      if (response.data.party) {
        setFormData(prev => ({ ...prev, tailorId: response.data.party.id.toString() }));
        setTailorSearchQuery('');
        setShowTailorDropdown(false);
      }
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleCreateWorkOrder = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    if (!hasBranches) {
      error('Create at least one branch before creating work orders. Go to Branches and add a branch.');
      setSubmitting(false);
      return;
    }
    if (isAdmin && !effectiveBranchId) {
      error('Select a branch from the header dropdown to create a work order.');
      setSubmitting(false);
      return;
    }

    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
      const branchId = user.role === 'manager' ? (user.branch_id || user.branchId) : effectiveBranchId;
      
      console.log('Creating work order with:', { businessId, branchId, formData });

      if (!businessId) {
        error('Business ID is required');
        setSubmitting(false);
        return;
      }

      if (!branchId) {
        error('Branch is required');
        setSubmitting(false);
        return;
      }

      if (!formData.tailorId) {
        error('Please select a tailor');
        setSubmitting(false);
        return;
      }

      if (!formData.inputProductId) {
        error('Please select input product');
        setSubmitting(false);
        return;
      }
      // Input variant validation - commented out for now
      // if (!formData.inputVariantId) {
      //   error('Please select input variant');
      //   setSubmitting(false);
      //   return;
      // }

      const hasVariants = (selectedInputProduct?.variants?.length > 0) || inputVariants.length > 0;
      const validItems = (formData.inputItems || []).filter(
        row => (hasVariants ? row.variantId : true) && parseFloat(row.quantity) > 0
      );
      if (validItems.length === 0) {
        error(hasVariants ? 'Add at least one variant with quantity' : 'Input quantity must be greater than 0');
        setSubmitting(false);
        return;
      }

      // Frontend validation: quantity must not exceed available stock per variant
      if (hasVariants) {
        for (const row of validItems) {
          const available = variantInventory[parseInt(row.variantId)] ?? 0;
          const requested = parseFloat(row.quantity || 0);
          if (requested > available) {
            const variant = inputVariants.find(v => v.id.toString() === row.variantId);
            const colorLabel = variant?.color || 'Variant';
            error(`${colorLabel}: cannot add ${requested} m — only ${available} m available`);
            setSubmitting(false);
            return;
          }
        }
      }

      if (formData.tailoringCost && parseFloat(formData.tailoringCost) < 0) {
        error('Stitching cost/bill amount cannot be negative');
        setSubmitting(false);
        return;
      }

      if (outputProductMode === 'existing' && !formData.outputProductId) {
        error('Select an existing output product (Our Creation) where received pieces will be added.');
        setSubmitting(false);
        return;
      }
      if (outputProductMode === 'new') {
        const name = (formData.outputProductName && String(formData.outputProductName).trim()) || '';
        const code = (formData.outputProductCode && String(formData.outputProductCode).trim()) || '';
        if (name.length < 2) {
          error('Product name is required (at least 2 characters) for the new Our Creation product.');
          setSubmitting(false);
          return;
        }
        if (!code) {
          error('Product code is required for the new Our Creation product. It must be unique within your business.');
          setSubmitting(false);
          return;
        }
        if (/\s/.test(code)) {
          error('Product code cannot contain spaces.');
          setSubmitting(false);
          return;
        }
      }

      const expectedQty = formData.expectedOutputQuantity && String(formData.expectedOutputQuantity).trim() !== '' ? parseInt(formData.expectedOutputQuantity, 10) : null;
      if (expectedQty == null || isNaN(expectedQty) || expectedQty < 1) {
        error('Enter expected output return (pieces). You can receive in parts later; the system will track received vs expected.');
        setSubmitting(false);
        return;
      }

      const productId = parseInt(formData.inputProductId);
      const costPerMeter = parseFloat(selectedInputProduct?.cost_price) || 0;
      const totalQuantity = validItems.reduce((sum, row) => sum + parseFloat(row.quantity || 0), 0);
      const firstItem = validItems[0];
      const payload = {
        branchId: parseInt(branchId),
        businessId: parseInt(businessId),
        tailorId: parseInt(formData.tailorId),
        inputProductId: productId,
        inputVariantId: hasVariants && firstItem.variantId ? parseInt(firstItem.variantId) : null,
        inputQuantity: totalQuantity,
        outputProductId: outputProductMode === 'existing' && formData.outputProductId ? parseInt(formData.outputProductId, 10) : null,
        outputProductName: outputProductMode === 'new' && formData.outputProductName?.trim() ? formData.outputProductName.trim() : undefined,
        outputProductCode: outputProductMode === 'new' && formData.outputProductCode?.trim() ? formData.outputProductCode.trim() : undefined,
        outputVariantId: null,
        expectedOutputQuantity: formData.expectedOutputQuantity && formData.expectedOutputQuantity !== '' ? parseInt(formData.expectedOutputQuantity) : null,
        tailoringCost: formData.tailoringCost && formData.tailoringCost !== '' ? parseFloat(formData.tailoringCost) : 0,
        notes: formData.notes && formData.notes !== '' ? formData.notes : undefined,
        items: hasVariants
          ? validItems.map(row => ({
              productId,
              variantId: parseInt(row.variantId),
              quantity: parseFloat(row.quantity),
              unitPrice: costPerMeter,
              itemType: 'input'
            }))
          : undefined
      };

      console.log('Sending API request:', payload);
      const response = await axios.post(`${API}/work-orders`, payload);
      console.log('Work order created:', response.data);
      success('Work order created successfully!');
      resetForm();
      fetchWorkOrders();
    } catch (err) {
      console.error('Error creating work order:', err);
      error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setFormData({
      tailorId: '',
      inputProductId: '',
      inputItems: [{ variantId: '', quantity: '' }],
      outputProductId: '',
      outputProductName: '',
      outputProductCode: '',
      tailoringCost: '',
      expectedOutputQuantity: '',
      notes: ''
    });
    setOutputProductMode('existing');
    setInputVariants([]);
    setProductInventory({});
    setTailorSearchQuery('');
    setShowTailorDropdown(false);
    setInputProductSearchQuery('');
    setShowInputProductDropdown(false);
    setSelectedOutputProduct(null);
    setOutputProductSearchQuery('');
    setOutputProductSearchResults([]);
    setShowOutputProductDropdown(false);
  };

  useEffect(() => {
    if (showModal) {
      if (user.role !== 'super_admin') {
        fetchBranches();
        fetchTailors();
        fetchProducts();
      }
    }
  }, [showModal, selectedBusinessId]);

  // Fetch inventory for header-selected branch when modal is open
  useEffect(() => {
    if (effectiveBranchId && showModal && inputProducts.length > 0) {
      fetchInventoryForProducts(inputProducts, effectiveBranchId);
    }
  }, [effectiveBranchId, showModal]);

  // Search stitched products for output (Our Creation) when user types
  useEffect(() => {
    if (!showModal || !outputProductSearchQuery.trim()) {
      setOutputProductSearchResults([]);
      return;
    }
    const t = setTimeout(() => {
      fetchOutputProductSearch(outputProductSearchQuery);
    }, 300);
    return () => clearTimeout(t);
  }, [showModal, outputProductSearchQuery]);

  const handleIssue = async (workOrder) => {
    try {
      console.log('Issuing work order:', workOrder.id);
      const response = await axios.post(`${API}/work-orders/${workOrder.id}/issue`);
      console.log('Work order issued:', response.data);
      success('Work order sent to tailor! Product inventory has been reduced.');
      setShowConfirmModal(false);
      setSelectedWorkOrder(null);
      setActionType(null);
      fetchWorkOrders();
      // Refresh products to update inventory display
      if (effectiveBranchId) {
        fetchProducts();
      }
    } catch (err) {
      console.error('Error issuing work order:', err);
      error(getErrorMessage(err));
      setShowConfirmModal(false);
    }
  };

  const handleReceive = async (workOrder, data) => {
    try {
      const { sizeBreakdown, receivedQuantity, billAmount } = data;

      const payload = {
        billAmount: parseFloat(billAmount)
      };
      if (sizeBreakdown && sizeBreakdown.length > 0) {
        payload.sizeBreakdown = sizeBreakdown;
        payload.receivedQuantity = receivedQuantity;
      } else {
        payload.receivedQuantity = parseInt(receivedQuantity);
        if (data.size) payload.size = data.size;
      }
      if (data.costPerItem != null) {
        payload.costPerItem = parseFloat(data.costPerItem) || 0;
      }
      if (data.costPerItemStitch != null) {
        payload.costPerItemStitch = parseFloat(data.costPerItemStitch) || 0;
      }
      if (data.outputProductId) {
        payload.outputProductId = data.outputProductId;
      }
      if (data.outputProductName && String(data.outputProductName).trim()) {
        payload.outputProductName = data.outputProductName.trim();
      }
      if (data.outputProductCode && String(data.outputProductCode).trim()) {
        payload.outputProductCode = data.outputProductCode.trim();
      }
      if (effectiveBranchId) {
        payload.branchId = effectiveBranchId;
      }

      const response = await axios.post(
        `${API}/work-orders/${workOrder.id}/receive`,
        payload
      );
      console.log('Work order received:', response.data);
      success(response.data?.work_order?.status === 'partially_received'
        ? 'Part received. Stitched units added to inventory. You can receive the remaining pieces later.'
        : 'Work order received successfully! Stitched units added to inventory.');
      setShowReceiveModal(false);
      setSelectedWorkOrder(null);
      fetchWorkOrders();
      // Refresh products to update inventory display
      if (effectiveBranchId) {
        fetchProducts();
      }
    } catch (err) {
      console.error('Error receiving work order:', err);
      error(getErrorMessage(err));
      setShowReceiveModal(false);
    }
  };

  const handleClose = async (workOrder) => {
    try {
      await axios.post(`${API}/work-orders/${workOrder.id}/close`);
      success('Work order closed successfully');
      fetchWorkOrders();
      setShowConfirmModal(false);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleCancel = async (workOrder) => {
    try {
      await axios.post(`${API}/work-orders/${workOrder.id}/cancel`);
      success('Work order cancelled');
      fetchWorkOrders();
      setShowConfirmModal(false);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleViewInvoice = async (workOrder) => {
    try {
      const response = await axios.get(`${API}/work-orders/${workOrder.id}/invoice`);
      setSelectedWorkOrder({ ...workOrder, invoice: response.data.invoice });
      setShowDetailsModal(true);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return '#6B7280';
      case 'issued': return '#3B82F6';
      case 'in_progress': return '#F59E0B';
      case 'partially_received': return '#8B5CF6';
      case 'received': return '#10B981';
      case 'closed': return '#059669';
      case 'cancelled': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const filteredWorkOrders = workOrders.filter(wo => {
    const q = (searchTerm || '').toLowerCase();
    if (!q) return true;
    return (
      wo.work_order_number?.toLowerCase().includes(q) ||
      (wo.vendor?.name || wo.input_product?.vendor?.name)?.toLowerCase().includes(q) ||
      wo.tailor?.name?.toLowerCase().includes(q) ||
      wo.batch_number?.toLowerCase().includes(q) ||
      wo.input_product?.product_code?.toLowerCase().includes(q) ||
      wo.output_product?.product_code?.toLowerCase().includes(q) ||
      wo.input_product?.name?.toLowerCase().includes(q) ||
      wo.output_product?.name?.toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <div className="work-orders-page">
        <div className="page-header">
          <h1>Work Orders</h1>
          {isMainBranch && (
            <Tooltip text={!hasBranches ? 'Create a branch first' : 'Create new work order'} position="bottom">
              <button 
                className="btn-primary" 
                onClick={(e) => {
                  e.preventDefault();
                  setShowModal(true);
                }}
                type="button"
                disabled={!hasBranches}
              >
                <HiPlus /> Create Work Order
              </button>
            </Tooltip>
          )}
        </div>

        {!isMainBranch && effectiveBranchId && (
          <div className="banner no-branches-banner" style={{ marginBottom: '1rem' }}>
            Only the main branch can use work orders.
            {currentBranch ? <> You are viewing <strong>{currentBranch.name}</strong>. Switch to the main branch in the header to create and manage work orders.</> : ' Switch to the main branch in the header.'}
          </div>
        )}
        {!hasBranches && (
          <div className="banner no-branches-banner" style={{ marginBottom: '1rem' }}>
            Create at least one branch before creating work orders. Go to <a href="/branches">Branches</a> to add a branch.
          </div>
        )}
        {user.role === 'super_admin' && isMainBranch && (
          <div className="filter-section">
            <select
              value={selectedBusinessId}
              onChange={(e) => setSelectedBusinessId(e.target.value)}
              className="form-select"
            >
              <option value="">Select Business</option>
              {businesses.map(business => (
                <option key={business.id} value={business.id}>{business.name}</option>
              ))}
            </select>
          </div>
        )}

        {isMainBranch && (
        <>
        <div className="filters">
          <div className="search-box">
            <HiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by work order #, vendor, tailor, batch, or product code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-select"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="issued">Issued</option>
            <option value="in_progress">In Progress</option>
            <option value="partially_received">Partially Received</option>
            <option value="received">Received</option>
            <option value="closed">Closed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {loading ? (
          <Loader message="Loading work orders..." variant="block" />
        ) : (
          <div className="work-orders-table-container">
            <table className="work-orders-table">
              <thead>
                <tr>
                  <th>Work Order #</th>
                  <th>Vendor</th>
                  <th>Tailor</th>
                  <th>Batch #</th>
                  <th>Input Product</th>
<th>Output Product</th>
                    <th>Pieces</th>
                    <th>Status</th>
                  <th>Total Cost</th>
                  <th>Cost per unit</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWorkOrders.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="no-data">No work orders found</td>
                  </tr>
                ) : (
                  filteredWorkOrders.map(wo => {
                    const receivedQty = wo.received_output_quantity ?? wo.receivedOutputQuantity;
                    const expectedQty = wo.expected_output_quantity ?? wo.expectedOutputQuantity;
                    const qtyForUnit = receivedQty ?? expectedQty ?? 0;
                    const costPerUnit = (parseFloat(wo.total_cost ?? wo.totalCost) || 0) / (qtyForUnit || 1);
                    return (
                    <tr key={wo.id}>
                      <td>{wo.work_order_number}</td>
                      <td>{wo.vendor?.name || wo.input_product?.vendor?.name || 'N/A'}</td>
                      <td>{wo.tailor?.name || 'N/A'}</td>
                      <td>{wo.batch_number || 'N/A'}</td>
                      <td>{wo.input_product?.name || 'N/A'}</td>
                      <td>{wo.output_product?.name || 'N/A'}</td>
                      <td className="pieces-cell">
                        {expectedQty != null ? `${receivedQty ?? 0} / ${expectedQty}` : (receivedQty ?? 0) || '—'}
                      </td>
                      <td>
                        <span 
                          className="status-badge" 
                          style={{ backgroundColor: getStatusColor(wo.status) }}
                        >
                          {wo.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </span>
                      </td>
                      <td>{formatPKR(wo.total_cost || 0)}</td>
                      <td className="cost-per-unit-cell">{qtyForUnit ? formatPKR(costPerUnit) : '—'}</td>
                      <td className="created-at-cell">{formatDate(wo.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <Tooltip text="View Traceability & Details">
                            <button
                              className="btn-icon"
                              onClick={async () => {
                                try {
                                  const response = await axios.get(`${API}/work-orders/${wo.id}`);
                                  setSelectedWorkOrder(response.data.work_order);
                                  setShowDetailsModal(true);
                                } catch (err) {
                                  error(getErrorMessage(err));
                                }
                              }}
                            >
                              <HiEye />
                            </button>
                          </Tooltip>
                          {wo.status === 'draft' && (
                            <Tooltip text="Send to Tailor">
                              <button
                                className="btn-icon btn-success"
                                onClick={() => {
                                  setSelectedWorkOrder(wo);
                                  setActionType('issue');
                                  setShowConfirmModal(true);
                                }}
                              >
                                <HiCheck />
                              </button>
                            </Tooltip>
                          )}
                          {(wo.status === 'issued' || wo.status === 'in_progress' || wo.status === 'partially_received') && (
                            <Tooltip text="Receive from Tailor (or receive next part)">
                              <button
                                className="btn-icon btn-success"
                                onClick={() => {
                                  setSelectedWorkOrder(wo);
                                  setActionType('receive');
                                  setShowConfirmModal(true);
                                }}
                              >
                                <HiCheck />
                              </button>
                            </Tooltip>
                          )}
                          {wo.status !== 'closed' && wo.status !== 'cancelled' && (
                            <Tooltip text="Cancel">
                              <button
                                className="btn-icon btn-danger"
                                onClick={() => {
                                  setSelectedWorkOrder(wo);
                                  setActionType('cancel');
                                  setShowConfirmModal(true);
                                }}
                              >
                                <HiX />
                              </button>
                            </Tooltip>
                          )}
                          <Tooltip text="Invoice">
                            <button
                              className="btn-icon"
                              onClick={() => handleViewInvoice(wo)}
                            >
                              <HiDocumentText />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  );})
                )}
              </tbody>
            </table>
          </div>
        )}

        </>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && selectedWorkOrder && (
          <ConfirmationModal
            isOpen={showConfirmModal}
            onClose={() => {
              setShowConfirmModal(false);
              setSelectedWorkOrder(null);
              setActionType(null);
            }}
            onConfirm={async () => {
              if (actionType === 'issue') {
                handleIssue(selectedWorkOrder);
              } else if (actionType === 'receive') {
                setShowConfirmModal(false);
                try {
                  const res = await axios.get(`${API}/work-orders/${selectedWorkOrder.id}`);
                  const fullWorkOrder = res.data.work_order || res.data;
                  setWorkOrderForReceiveModal(fullWorkOrder);
                  setShowReceiveModal(true);
                } catch (err) {
                  error(getErrorMessage(err));
                  setSelectedWorkOrder(null);
                }
              } else if (actionType === 'close') {
                handleClose(selectedWorkOrder);
              } else if (actionType === 'cancel') {
                handleCancel(selectedWorkOrder);
              }
            }}
            title={
              actionType === 'issue' ? 'Send to Tailor' :
              actionType === 'receive' ? 'Receive from Tailor' :
              `${actionType?.charAt(0).toUpperCase() + actionType?.slice(1)} Work Order`
            }
            message={
              actionType === 'issue' 
                ? `Send work order ${selectedWorkOrder.work_order_number} to tailor? This will move inventory to "In Progress / At Tailor" state.`
                : actionType === 'receive'
                ? `Receive finished units from tailor for work order ${selectedWorkOrder.work_order_number}?`
                : `Are you sure you want to ${actionType} work order ${selectedWorkOrder.work_order_number}?`
            }
            notes={actionType === 'receive' ? selectedWorkOrder.notes : undefined}
            confirmText={
              actionType === 'issue' ? 'Send to Tailor' :
              actionType === 'receive' ? 'Receive' :
              actionType?.charAt(0).toUpperCase() + actionType?.slice(1)
            }
          />
        )}

        {/* Receive Work Order Modal - uses full work order (with output_product + meta) so product from creation is shown */}
        {showReceiveModal && workOrderForReceiveModal && (
          <ReceiveWorkOrderModal
            isOpen={showReceiveModal}
            onClose={() => {
              setShowReceiveModal(false);
              setWorkOrderForReceiveModal(null);
              setSelectedWorkOrder(null);
            }}
            onConfirm={(data) => handleReceive(workOrderForReceiveModal, data)}
            workOrder={workOrderForReceiveModal}
            defaultQuantity={(() => {
                const expected = workOrderForReceiveModal.expected_output_quantity ?? workOrderForReceiveModal.expectedOutputQuantity;
                const received = workOrderForReceiveModal.received_output_quantity ?? workOrderForReceiveModal.receivedOutputQuantity ?? 0;
                if (expected != null && received > 0) return Math.max(0, expected - received);
                return expected ?? workOrderForReceiveModal.input_quantity ?? 0;
              })()}
            defaultBillAmount={workOrderForReceiveModal.tailoring_cost || 0}
            apiBase={API}
            businessId={user?.role === 'super_admin' ? selectedBusinessId : (user?.business_id || user?.businessId)}
            branchId={effectiveBranchId}
          />
        )}

        {/* Create Work Order Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => !submitting && resetForm()}>
            <div className="modal-content create-work-order-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Create Work Order</h2>
                <button className="btn-close" onClick={() => !submitting && resetForm()}>×</button>
              </div>
              <form onSubmit={handleCreateWorkOrder}>
                <div className="modal-body create-wo-body">
                  {currentBranch && (
                    <div className="create-wo-branch-badge">
                      <span className="create-wo-branch-label">Branch</span>
                      <span className="create-wo-branch-name">{currentBranch.name}</span>
                    </div>
                  )}

                  <div className="form-section create-wo-section">
                    <h3 className="create-wo-section-title">Tailor</h3>
                    <div className="form-group tailor-search-wrapper" ref={tailorDropdownRef}>
                      <label>Tailor *</label>
                      <div className="tailor-search-row">
                        <div className="tailor-search-input-wrapper">
                          <span className="tailor-search-icon-slot" aria-hidden="true">
                            <HiSearch className="tailor-search-icon" />
                          </span>
                          <input
                            type="text"
                            className="tailor-search-input"
                            placeholder="Search tailors by name, city, or phone..."
                            value={selectedTailor ? tailorDisplayText : tailorSearchQuery}
                            onChange={(e) => {
                              setTailorSearchQuery(e.target.value);
                              setShowTailorDropdown(true);
                              setFormData(prev => ({ ...prev, tailorId: '' }));
                            }}
                            onFocus={() => setShowTailorDropdown(true)}
                            disabled={submitting}
                            readOnly={!!selectedTailor}
                          />
                          {selectedTailor && (
                            <button
                              type="button"
                              className="tailor-clear-btn"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, tailorId: '' }));
                                setTailorSearchQuery('');
                                setShowTailorDropdown(true);
                              }}
                              disabled={submitting}
                              title="Clear selection"
                            >
                              <HiX />
                            </button>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowAddTailorModal(true)}
                          className="btn-add-tailor"
                          disabled={submitting}
                          title="Add New Tailor"
                        >
                          <HiPlus /> Add
                        </button>
                      </div>
                      {showTailorDropdown && !selectedTailor && (
                        <div className="tailor-dropdown">
                          {filteredTailors.length === 0 ? (
                            <div className="tailor-dropdown-empty">
                              No tailors found. Try a different search or add a new tailor.
                            </div>
                          ) : (
                            filteredTailors.map(tailor => (
                              <div
                                key={tailor.id}
                                className="tailor-dropdown-item"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, tailorId: tailor.id.toString() }));
                                  setTailorSearchQuery('');
                                  setShowTailorDropdown(false);
                                }}
                              >
                                <span className="tailor-dropdown-name">{tailor.name}</span>
                                <span className="tailor-dropdown-meta">
                                  {[tailor.city, tailor.phone].filter(Boolean).join(' · ')}
                                  {(tailor.city || tailor.phone) && ' · '}
                                  <span className={partySearchBalanceClassName(tailor.balance, tailor.is_shop_expense)}>
                                    Balance: {formatPartySearchBalanceLabel(tailor.balance, tailor.is_shop_expense)}
                                  </span>
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                      <small className="form-hint">Search by name, city, or phone. One tailor per work order.</small>
                    </div>
                  </div>

                  <div className="form-section create-wo-section">
                    <h3 className="create-wo-section-title">Input Material (Unstitched)</h3>
                    <div className="form-grid">
                      <div className="form-group input-product-search-wrapper" ref={inputProductDropdownRef}>
                        <label>Input Product *</label>
                        <div className="input-product-search-row">
                          <div className="input-product-search-input-wrapper">
                            <span className="input-product-search-icon-slot" aria-hidden="true">
                              <HiSearch className="input-product-search-icon" />
                            </span>
                            <input
                              type="text"
                              className="input-product-search-input"
                              placeholder="Search unstitched products by name or code..."
                              value={selectedInputProduct ? inputProductDisplayText : inputProductSearchQuery}
                              onChange={(e) => {
                                setInputProductSearchQuery(e.target.value);
                                setShowInputProductDropdown(true);
                                setFormData(prev => ({ ...prev, inputProductId: '', inputVariantId: '', inputItems: [{ variantId: '', quantity: '' }] }));
                              }}
                              onFocus={() => setShowInputProductDropdown(true)}
                              disabled={submitting}
                              readOnly={!!selectedInputProduct}
                              required={!formData.inputProductId}
                            />
                            {selectedInputProduct && (
                              <button
                                type="button"
                                className="input-product-clear-btn"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, inputProductId: '', inputVariantId: '', inputItems: [{ variantId: '', quantity: '' }] }));
                                  setInputProductSearchQuery('');
                                  setShowInputProductDropdown(true);
                                }}
                                disabled={submitting}
                                title="Clear selection"
                              >
                                <HiX />
                              </button>
                            )}
                          </div>
                        </div>
                        {showInputProductDropdown && !selectedInputProduct && (
                          <div className="input-product-dropdown">
                            {filteredInputProducts.length === 0 ? (
                              <div className="input-product-dropdown-empty">
                                {inputProducts.length === 0
                                  ? 'No unstitched products found. Add products first.'
                                  : 'No matching products. Try a different search.'}
                              </div>
                            ) : (
                              filteredInputProducts.map(product => {
                                const availableMeters = productInventory[product.id] ?? 0;
                                return (
                                  <div
                                    key={product.id}
                                    className="input-product-dropdown-item"
                                    onClick={() => {
                                      setFormData(prev => ({ ...prev, inputProductId: product.id.toString(), inputItems: [{ variantId: '', quantity: '' }] }));
                                      setInputProductSearchQuery('');
                                      setShowInputProductDropdown(false);
                                      if (effectiveBranchId) {
                                        fetchInventoryForProduct(product.id, effectiveBranchId);
                                      }
                                    }}
                                  >
                                    <span className="input-product-dropdown-name">{product.name} ({product.product_code})</span>
                                    <span className="input-product-dropdown-meta">
                                      {effectiveBranchId
                                        ? `${availableMeters.toFixed(2)} guz available`
                                        : 'Select branch in header for stock'}
                                    </span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                        {formData.inputProductId && productInventory[formData.inputProductId] !== undefined && !(selectedInputProduct?.variants?.length > 0) && (
                          <small className="form-hint" style={{ 
                            color: productInventory[formData.inputProductId] > 0 ? '#059669' : '#EF4444',
                            fontWeight: '500'
                          }}>
                            Available: {productInventory[formData.inputProductId].toFixed(2)} guz
                          </small>
                        )}
                        <small className="form-hint">Stock shown is for the branch selected in the header.</small>
                      </div>
                      {(inputVariants.length > 0 || selectedInputProduct?.variants?.length > 0) ? (
                        <div className="form-group input-variants-group">
                          <label>Variants (Color + Guz) *</label>
                          <span className="form-hint">Add each color and quantity in guz. Stock is for the branch in the header.</span>
                          {formData.inputItems?.map((row, idx) => (
                            <div key={idx} className="input-variant-row">
                              <VariantSelect
                                variants={inputVariants}
                                variantInventory={variantInventory}
                                value={row.variantId}
                                onChange={(variantId) => {
                                  const available = variantInventory[variantId] ?? 0;
                                  const currentQty = parseFloat(row.quantity || 0);
                                  const next = [...(formData.inputItems || [])];
                                  next[idx] = {
                                    ...next[idx],
                                    variantId: variantId.toString(),
                                    quantity: currentQty > available ? (available > 0 ? String(available) : '') : row.quantity
                                  };
                                  setFormData(prev => ({ ...prev, inputItems: next }));
                                }}
                                placeholder="Search by color..."
                                disabled={submitting}
                                showAllVariants
                                excludeVariantIds={formData.inputItems
                                  ?.filter((r, i) => i !== idx && r.variantId)
                                  .map(r => parseInt(r.variantId))
                                  .filter(Boolean) || []}
                              />
                              <div className="variant-qty-input-wrap">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0.01"
                                  max={row.variantId ? (variantInventory[parseInt(row.variantId)] ?? 0) : undefined}
                                  placeholder="Guz"
                                  value={row.quantity}
                                  onChange={(e) => {
                                    const next = [...(formData.inputItems || [])];
                                    next[idx] = { ...next[idx], quantity: e.target.value };
                                    setFormData(prev => ({ ...prev, inputItems: next }));
                                  }}
                                  disabled={submitting}
                                  required
                                  title={row.variantId ? `Max ${(variantInventory[parseInt(row.variantId)] ?? 0).toFixed(2)} m available` : ''}
                                  className={row.variantId && parseFloat(row.quantity || 0) > (variantInventory[parseInt(row.variantId)] ?? 0) ? 'input-exceeds-stock' : ''}
                                />
                                {row.variantId && parseFloat(row.quantity || 0) > (variantInventory[parseInt(row.variantId)] ?? 0) && (
                                  <span className="variant-qty-error">
                                    Only {(variantInventory[parseInt(row.variantId)] ?? 0).toFixed(2)} m available — reduce quantity
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                className="btn-remove-variant-wo"
                                onClick={() => setFormData(prev => ({
                                  ...prev,
                                  inputItems: (prev.inputItems || []).filter((_, i) => i !== idx)
                                }))}
                                disabled={submitting || (formData.inputItems?.length || 0) <= 1}
                                title="Remove"
                              >
                                <HiX />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="btn-add-variant-wo"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              inputItems: [...(prev.inputItems || []), { variantId: '', quantity: '' }]
                            }))}
                            disabled={submitting}
                          >
                            <HiPlus /> Add Variant
                          </button>
                        </div>
                      ) : (
                        <div className="form-group">
                          <label>Input Quantity (guz) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={formData.inputItems?.[0]?.quantity ?? ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              inputItems: [{ variantId: '', quantity: e.target.value }]
                            }))}
                            required
                            disabled={submitting}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-section create-wo-section">
                    <h3 className="create-wo-section-title">Output Product (Our Creation)</h3>
                    <p className="form-hint create-wo-output-hint">Choose where received pieces will be added: an existing stitched product or a new product (created when you receive).</p>
                    <div className="receive-output-mode-tabs" style={{ marginBottom: '12px' }}>
                      <button
                        type="button"
                        className={`receive-mode-tab ${outputProductMode === 'existing' ? 'active' : ''}`}
                        onClick={() => {
                          setOutputProductMode('existing');
                          setFormData(prev => ({ ...prev, outputProductName: '', outputProductCode: '' }));
                        }}
                      >
                        Add to existing
                      </button>
                      <button
                        type="button"
                        className={`receive-mode-tab ${outputProductMode === 'new' ? 'active' : ''}`}
                        onClick={() => {
                          setOutputProductMode('new');
                          setFormData(prev => ({ ...prev, outputProductId: '' }));
                          setSelectedOutputProduct(null);
                          setOutputProductSearchQuery('');
                          setOutputProductSearchResults([]);
                        }}
                      >
                        Add new product
                      </button>
                    </div>

                    {outputProductMode === 'existing' && (
                      <div className="form-group output-product-search-wrapper" ref={outputProductDropdownRef}>
                        <label>Select existing product *</label>
                        <div className="input-product-search-row">
                          <div className="input-product-search-input-wrapper">
                            <span className="input-product-search-icon-slot" aria-hidden="true">
                              <HiSearch className="input-product-search-icon" />
                            </span>
                            <input
                              type="text"
                              className="input-product-search-input"
                              placeholder="Search stitched products by name or code..."
                              value={selectedOutputProduct ? outputProductDisplayText : outputProductSearchQuery}
                              onChange={(e) => {
                                setOutputProductSearchQuery(e.target.value);
                                setShowOutputProductDropdown(true);
                                setFormData(prev => ({ ...prev, outputProductId: '' }));
                                setSelectedOutputProduct(null);
                              }}
                              onFocus={() => setShowOutputProductDropdown(true)}
                              disabled={!!selectedOutputProduct || submitting}
                              readOnly={!!selectedOutputProduct}
                            />
                            {selectedOutputProduct && (
                              <button
                                type="button"
                                className="input-product-clear-btn"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, outputProductId: '' }));
                                  setSelectedOutputProduct(null);
                                  setOutputProductSearchQuery('');
                                  setShowOutputProductDropdown(true);
                                }}
                                disabled={submitting}
                                title="Clear selection"
                              >
                                <HiX />
                              </button>
                            )}
                          </div>
                        </div>
                        {showOutputProductDropdown && !selectedOutputProduct && (
                          <div className="input-product-dropdown">
                            {outputProductSearchLoading && <div className="input-product-dropdown-empty">Searching…</div>}
                            {!outputProductSearchLoading && outputProductSearchQuery.trim().length < 2 && (
                              <div className="input-product-dropdown-empty">Type at least 2 characters to search stitched products.</div>
                            )}
                            {!outputProductSearchLoading && outputProductSearchQuery.trim().length >= 2 && outputProductSearchResults.length === 0 && (
                              <div className="input-product-dropdown-empty">No stitched products found. Add a stitched product in Products first.</div>
                            )}
                            {!outputProductSearchLoading && outputProductSearchResults.length > 0 && (
                              outputProductSearchResults.map((product) => (
                                <div
                                  key={product.id}
                                  className="input-product-dropdown-item"
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, outputProductId: product.id.toString() }));
                                    setSelectedOutputProduct(product);
                                    setOutputProductSearchQuery('');
                                    setShowOutputProductDropdown(false);
                                  }}
                                >
                                  <span className="input-product-dropdown-name">{product.name} ({(product.product_code || product.productCode) || '—'})</span>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {outputProductMode === 'new' && (
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Product name *</label>
                          <input
                            type="text"
                            className="create-wo-input"
                            placeholder="e.g. Men's Formal Shirt"
                            value={formData.outputProductName}
                            onChange={(e) => setFormData(prev => ({ ...prev, outputProductName: e.target.value }))}
                            disabled={submitting}
                            maxLength={100}
                          />
                        </div>
                        <div className="form-group">
                          <label>Product code *</label>
                          <input
                            type="text"
                            className="create-wo-input"
                            placeholder="e.g. SHIRT-001"
                            value={formData.outputProductCode}
                            onChange={(e) => setFormData(prev => ({ ...prev, outputProductCode: e.target.value }))}
                            disabled={submitting}
                            maxLength={50}
                            required
                          />
                          <small className="form-hint">Required. Must be unique within your business. No spaces.</small>
                        </div>
                      </div>
                    )}

                    <div className="form-group" style={{ marginTop: 16 }}>
                      <label>Expected output return (pieces) *</label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        className="create-wo-input"
                        placeholder="e.g. 10"
                        value={formData.expectedOutputQuantity}
                        onChange={(e) => setFormData(prev => ({ ...prev, expectedOutputQuantity: e.target.value }))}
                        disabled={submitting}
                      />
                      <small className="form-hint">How many pieces you expect back from this work order. You can receive in parts; the system will track received vs expected.</small>
                    </div>
                  </div>

                  <div className="form-section create-wo-section">
                    <h3 className="create-wo-section-title">Cost & Notes</h3>
                    <div className="form-row create-wo-cost-notes">
                      <div className="form-group create-wo-stitching-cost-hidden">
                        <label>Stitching cost / bill amount (Rs.)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.tailoringCost}
                          onChange={(e) => setFormData(prev => ({ ...prev, tailoringCost: e.target.value }))}
                          placeholder="Optional – enter tailor bill amount"
                          disabled={submitting}
                          className="create-wo-input"
                        />
                      </div>
                      <div className="form-group create-wo-notes-wrap">
                        <label>Notes</label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                          placeholder="Additional notes..."
                          rows="2"
                          disabled={submitting}
                          className="create-wo-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer create-wo-footer">
                  <button type="button" className="btn-secondary" onClick={resetForm} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={submitting}>
                    {submitting ? 'Creating...' : 'Create Work Order'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Tailor Modal */}
        {showAddTailorModal && (
          <div className="modal-overlay" onClick={() => setShowAddTailorModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add New Tailor</h2>
                <button className="btn-close" onClick={() => setShowAddTailorModal(false)}>×</button>
              </div>
              <form onSubmit={handleCreateTailor}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Tailor Name *</label>
                    <input
                      type="text"
                      value={tailorFormData.name}
                      onChange={(e) => setTailorFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Ahmed Tailor"
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="text"
                        value={tailorFormData.phone}
                        onChange={(e) => setTailorFormData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="e.g. +92 300 1234567"
                      />
                    </div>
                    <div className="form-group">
                      <label>City *</label>
                      <CitySelect
                        value={tailorFormData.city}
                        onChange={(city) => setTailorFormData(prev => ({ ...prev, city }))}
                        required
                        placeholder="Select city"
                        className="form-input"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <textarea
                      value={tailorFormData.address}
                      onChange={(e) => setTailorFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Full address..."
                      rows="2"
                    />
                  </div>
                  <div className="form-group opening-balance-group">
                    <label>Opening Balance</label>
                    <div className="opening-balance-options">
                      <label className={`opening-balance-option opening-balance-option-received ${tailorFormData.openingBalanceDirection === 'they_owe_you' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="tailorOpeningBalanceDirection"
                          checked={tailorFormData.openingBalanceDirection === 'they_owe_you'}
                          onChange={() => setTailorFormData(prev => ({ ...prev, openingBalanceDirection: 'they_owe_you' }))}
                        />
                        <span className="option-text">
                          <span className="option-label">Payment Received</span>
                        </span>
                      </label>
                      <label className={`opening-balance-option opening-balance-option-made ${tailorFormData.openingBalanceDirection === 'you_owe_them' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="tailorOpeningBalanceDirection"
                          checked={tailorFormData.openingBalanceDirection === 'you_owe_them'}
                          onChange={() => setTailorFormData(prev => ({ ...prev, openingBalanceDirection: 'you_owe_them' }))}
                        />
                        <span className="option-text">
                          <span className="option-label">Payment Made</span>
                        </span>
                      </label>
                    </div>
                    <div className={`opening-balance-amount-row opening-balance-amount-${tailorFormData.openingBalanceDirection === 'they_owe_you' ? 'received' : 'made'}`}>
                      <label className="amount-label">Amount (PKR)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={tailorFormData.openingBalanceAmount}
                        onChange={(e) => setTailorFormData(prev => ({ ...prev, openingBalanceAmount: e.target.value }))}
                        placeholder="0.00"
                        className="opening-balance-amount-input"
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => setShowAddTailorModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Add Tailor
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {showDetailsModal && selectedWorkOrder && (
          <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
            <div className="modal-content work-order-details-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Work Order Details</h2>
                <button className="btn-close" onClick={() => setShowDetailsModal(false)}>×</button>
              </div>
              <div className="modal-body">
                {/* Traceability Chain - Most Important Section */}
                <div className="detail-section traceability-section">
                  <h3>📋 Traceability Chain</h3>
                  <div className="traceability-chain">
                    <div className="traceability-step">
                      <div className="step-icon">🏭</div>
                      <div className="step-content">
                        <div className="step-label">Vendor (Supplier)</div>
                        <div className="step-value">{selectedWorkOrder.vendor?.name || selectedWorkOrder.input_product?.vendor?.name || 'N/A'}</div>
                        {selectedWorkOrder.purchase?.purchase_number && (
                          <div className="step-detail">Purchase: {selectedWorkOrder.purchase.purchase_number}</div>
                        )}
                      </div>
                    </div>
                    <div className="traceability-arrow">→</div>
                    <div className="traceability-step">
                      <div className="step-icon">📦</div>
                      <div className="step-content">
                        <div className="step-label">Vendor Batch</div>
                        <div className="step-value">{selectedWorkOrder.batch_number || 'N/A'}</div>
                        <div className="step-detail">
                          {(() => {
                            const inputItems = (selectedWorkOrder.items || []).filter(i => (i.item_type || i.itemType) === 'input');
                            if (inputItems.length > 1) {
                              const totalGuz = inputItems.reduce((s, i) => s + (parseFloat(i.quantity) || 0), 0);
                              return `${inputItems.length} variants, ${totalGuz} guz total`;
                            }
                            if (inputItems.length === 1) {
                              const v = inputItems[0];
                              const variantLabel = [v.variant?.color, v.variant?.size].filter(Boolean).filter(x => String(x).toLowerCase() !== 'default').join(' / ') || 'One';
                              return `${selectedWorkOrder.input_product?.name || v.product?.name || 'N/A'} (${variantLabel}) · ${v.quantity} guz`;
                            }
                            return `${selectedWorkOrder.input_product?.name || 'N/A'} - ${selectedWorkOrder.input_quantity} guz`;
                          })()}
                        </div>
                      </div>
                    </div>
                    <div className="traceability-arrow">→</div>
                    <div className="traceability-step">
                      <div className="step-icon">✂️</div>
                      <div className="step-content">
                        <div className="step-label">Tailor</div>
                        <div className="step-value">{selectedWorkOrder.tailor?.name || 'N/A'}</div>
                        <div className="step-detail">Work Order: {selectedWorkOrder.work_order_number}</div>
                      </div>
                    </div>
                    <div className="traceability-arrow">→</div>
                    <div className="traceability-step">
                      <div className="step-icon">✅</div>
                      <div className="step-content">
                        <div className="step-label">Stitched Units</div>
                        <div className="step-value">
                          {selectedWorkOrder.received_output_quantity || selectedWorkOrder.expected_output_quantity || 0} pieces
                        </div>
                        <div className="step-detail">
                          {selectedWorkOrder.output_product?.name || 'N/A'} - {selectedWorkOrder.output_variant?.color || ''} {selectedWorkOrder.output_variant?.size || ''}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Basic Information</h3>
                  <div className="detail-grid">
                    <div><strong>Work Order #:</strong> {selectedWorkOrder.work_order_number}</div>
                    <div><strong>Batch #:</strong> {selectedWorkOrder.batch_number || 'N/A'}</div>
                    <div><strong>Status:</strong> 
                      <span className="status-badge" style={{ backgroundColor: getStatusColor(selectedWorkOrder.status) }}>
                        {selectedWorkOrder.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <div><strong>Vendor:</strong> {selectedWorkOrder.vendor?.name || selectedWorkOrder.input_product?.vendor?.name || 'N/A'}</div>
                    <div><strong>Tailor:</strong> {selectedWorkOrder.tailor?.name || 'N/A'}</div>
                    <div><strong>Branch:</strong> {selectedWorkOrder.branch?.name || 'N/A'}</div>
                    {selectedWorkOrder.purchase?.purchase_number && (
                      <div><strong>Purchase Order:</strong> {selectedWorkOrder.purchase.purchase_number}</div>
                    )}
                    {selectedWorkOrder.issue_date && (
                      <div><strong>Issue Date:</strong> {new Date(selectedWorkOrder.issue_date).toLocaleDateString()}</div>
                    )}
                    {selectedWorkOrder.received_date && (
                      <div><strong>Received Date:</strong> {new Date(selectedWorkOrder.received_date).toLocaleDateString()}</div>
                    )}
                    {selectedWorkOrder.closed_date && (
                      <div><strong>Closed Date:</strong> {new Date(selectedWorkOrder.closed_date).toLocaleDateString()}</div>
                    )}
                  </div>
                </div>
                <div className="detail-section">
                  <h3>Input Material (Unstitched)</h3>
                  {(() => {
                    const inputItems = (selectedWorkOrder.items || []).filter(i => (i.item_type || i.itemType) === 'input');
                    if (inputItems.length > 0) {
                      return (
                        <>
                          <div className="wo-detail-table-wrapper">
                            <table className="wo-detail-table">
                              <thead>
                                <tr>
                                  <th>Product</th>
                                  <th>Variant</th>
                                  <th className="wo-detail-num">Qty (guz)</th>
                                  <th className="wo-detail-num">Cost / unit</th>
                                  <th className="wo-detail-num">Line total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {inputItems.map((item, idx) => {
                                  const productName = item.product?.name || selectedWorkOrder.input_product?.name || '—';
                                  const productCode = item.product?.product_code || item.product?.productCode || selectedWorkOrder.input_product?.product_code || '—';
                                  const variantLabel = [item.variant?.color, item.variant?.size].filter(Boolean).filter(x => String(x).toLowerCase() !== 'default').join(' / ') || 'One';
                                  const qty = parseFloat(item.quantity) || 0;
                                  const unitPrice = parseFloat(item.unit_price ?? item.unitPrice) || 0;
                                  const lineTotal = parseFloat(item.total_price ?? item.totalPrice) || qty * unitPrice;
                                  return (
                                    <tr key={item.id || idx}>
                                      <td>
                                        <span className="wo-detail-product-name">{productName}</span>
                                        {productCode && productCode !== '—' && <span className="wo-detail-product-code"> ({productCode})</span>}
                                      </td>
                                      <td>{variantLabel}</td>
                                      <td className="wo-detail-num">{qty}</td>
                                      <td className="wo-detail-num">{formatPKR(unitPrice)}</td>
                                      <td className="wo-detail-num">{formatPKR(lineTotal)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr className="wo-detail-total-row">
                                  <td colSpan={4}><strong>Total material cost</strong></td>
                                  <td className="wo-detail-num"><strong>{formatPKR(selectedWorkOrder.material_cost ?? 0)}</strong></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                          <div className="detail-grid detail-grid-extra">
                            <div><strong>Vendor:</strong> {selectedWorkOrder.vendor?.name || selectedWorkOrder.input_product?.vendor?.name || 'N/A'}</div>
                          </div>
                        </>
                      );
                    }
                    return (
                      <div className="detail-grid">
                        <div><strong>Product:</strong> {selectedWorkOrder.input_product?.name || 'N/A'}</div>
                        <div><strong>Product Code:</strong> {selectedWorkOrder.input_product?.product_code || 'N/A'}</div>
                        <div><strong>Variant:</strong> {[selectedWorkOrder.input_variant?.color, selectedWorkOrder.input_variant?.size].filter(Boolean).filter(x => String(x).toLowerCase() !== 'default').join(' / ') || 'N/A'}</div>
                        <div><strong>Quantity:</strong> {selectedWorkOrder.input_quantity} guz</div>
                        <div><strong>Material Cost:</strong> {formatPKR(selectedWorkOrder.material_cost ?? 0)}</div>
                        <div><strong>Vendor:</strong> {selectedWorkOrder.vendor?.name || selectedWorkOrder.input_product?.vendor?.name || 'N/A'}</div>
                      </div>
                    );
                  })()}
                </div>
                <div className="detail-section">
                  <h3>Output Product (Stitched)</h3>
                  <div className="detail-grid">
                    <div><strong>Product:</strong> {selectedWorkOrder.output_product?.name || 'N/A'}</div>
                    <div><strong>Product Code:</strong> {selectedWorkOrder.output_product?.product_code || 'N/A'}</div>
                    <div><strong>Variant:</strong> {[selectedWorkOrder.output_variant?.color, selectedWorkOrder.output_variant?.size].filter(Boolean).filter(x => String(x).toLowerCase() !== 'default').join(' / ') || 'N/A'}</div>
                    <div><strong>Expected items:</strong> {selectedWorkOrder.expected_output_quantity != null && selectedWorkOrder.expected_output_quantity !== '' ? `${selectedWorkOrder.expected_output_quantity} pieces` : '—'}</div>
                    <div><strong>Received:</strong> {selectedWorkOrder.received_output_quantity ?? 0} pieces</div>
                    <div><strong>Vendor:</strong> {selectedWorkOrder.output_product?.vendor?.name || 'N/A'}</div>
                    <div><strong>Tailoring Cost:</strong> {formatPKR(selectedWorkOrder.tailoring_cost || 0)}</div>
                    <div><strong>Additional cost (per unit):</strong> {formatPKR(selectedWorkOrder.additional_expense_per_unit ?? selectedWorkOrder.additionalExpensePerUnit ?? 0)} <span className="detail-muted">— entered when receiving</span></div>
                    {(parseFloat(selectedWorkOrder.additional_expense_per_unit ?? selectedWorkOrder.additionalExpensePerUnit ?? 0) > 0 && (selectedWorkOrder.received_output_quantity ?? 0) > 0) && (
                      <div><strong>Additional cost (total):</strong> {formatPKR(parseFloat(selectedWorkOrder.additional_expense_per_unit ?? selectedWorkOrder.additionalExpensePerUnit ?? 0) * (selectedWorkOrder.received_output_quantity ?? 0))}</div>
                    )}
                    <div><strong>Total Cost:</strong> {formatPKR(selectedWorkOrder.total_cost || 0)}</div>
                    {selectedWorkOrder.received_output_quantity > 0 && (() => {
                      const material = parseFloat(selectedWorkOrder.material_cost ?? 0) || 0;
                      const tailoring = parseFloat(selectedWorkOrder.tailoring_cost ?? 0) || 0;
                      const additionalPerUnit = parseFloat(selectedWorkOrder.additional_expense_per_unit ?? selectedWorkOrder.additionalExpensePerUnit ?? 0) || 0;
                      const received = parseFloat(selectedWorkOrder.received_output_quantity ?? 0) || 1;
                      const additionalTotal = additionalPerUnit * received;
                      const costPerUnit = (material + tailoring + additionalTotal) / received;
                      return (
                        <div><strong>Cost per Unit:</strong> {formatPKR(costPerUnit)} <span className="detail-muted">(Material + Tailoring + Additional total) ÷ {received} pieces</span></div>
                      );
                    })()}
                  </div>
                </div>
                {selectedWorkOrder.notes && (
                  <div className="detail-section">
                    <h3>Notes</h3>
                    <p>{selectedWorkOrder.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default WorkOrders;
